import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import FeedbackModal from "../pro_calculator/general/FeedbackModal";
import { BaseSidebar } from "./BaseSidebar";
import { SidebarItem } from "./SidebarItem";
import { SidebarSection } from "./SidebarSection";
import { useAuth } from "../router/authContext";
import { hasProductAccess } from "../../utils/productAccess";

const actionVariants = {
  hidden: { opacity: 0, y: 4 },
  visible: { opacity: 1, y: 0 },
};

export const AppSidebar = ({
  navConfig,
  collapsed,
  toggleCollapse,
  isMobile = false,
  mobileOpen = false,
  closeMobile,
}) => {
  const { currentUser } = useAuth();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const canAccessAmptransInternalItems = String(currentUser?.email || "")
    .trim()
    .toLowerCase()
    .endsWith("@amptrans.net");
  const sections = (navConfig?.sections || [])
    .map((section) => ({
      ...section,
      items: (section.items || []).filter((item) => {
        if (item?.requiresAmptransEmail && !canAccessAmptransInternalItems) return false;
        if (item?.requiresProductAccess && !hasProductAccess(currentUser, item.requiresProductAccess)) {
          return false;
        }
        return true;
      }),
    }))
    .filter((section) => (section.items || []).length > 0);
  const resources = (navConfig?.resources || []).filter((item) => {
    if (item?.requiresProductAccess && !hasProductAccess(currentUser, item.requiresProductAccess)) {
      return false;
    }
    return true;
  });
  const actions = navConfig?.actions || [];
  const homeItem = navConfig?.homeItem;
  const productName = navConfig?.productName || "eFleetPlan";
  const feedbackStepOptions = navConfig?.feedbackStepOptions;

  const showCollapsedTooltips = collapsed && !isMobile;

  const actionItems = actions.map((action) => {
    const Icon = action.icon;
    return (
      <motion.button
        key={action.key}
        type="button"
        variants={actionVariants}
        initial="hidden"
        animate="visible"
        className={`group relative my-1 flex w-full items-center rounded-lg px-3 py-3 text-sm text-gray-700 transition-all duration-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          showCollapsedTooltips ? "justify-center" : "gap-3"
        }`}
        onClick={() => {
          if (action.type === "feedback") {
            setIsFeedbackOpen(true);
          }
        }}
      >
        <div className="flex-shrink-0 text-gray-600">
          <Icon size={20} />
        </div>
        {showCollapsedTooltips ? null : <span className="font-ui font-medium">{action.text}</span>}
        {showCollapsedTooltips ? (
          <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            {action.text}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
          </div>
        ) : null}
      </motion.button>
    );
  });

  return (
    <>
      <BaseSidebar
        collapsed={collapsed}
        toggleCollapse={toggleCollapse}
        additionalResources={resources}
        bottomChildren={actionItems.length > 0 ? actionItems : null}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        closeMobile={closeMobile}
      >
        {homeItem ? (
          <div className="pt-2">
            <SidebarItem
              collapsed={collapsed}
              isMobile={isMobile}
              icon={React.createElement(homeItem.icon, { size: 20 })}
              text={homeItem.text}
              link={homeItem.link}
            />
          </div>
        ) : null}

        <AnimatePresence>
          {sections.map((section) => (
            <SidebarSection key={section.label} label={section.label} collapsed={collapsed} isMobile={isMobile}>
              {section.items.map((item) => (
                <motion.div
                  key={item.link}
                  variants={actionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.2 }}
                >
                  <SidebarItem
                    collapsed={collapsed}
                    isMobile={isMobile}
                    icon={React.createElement(item.icon, { size: 20 })}
                    text={item.text}
                    link={item.link}
                    badge={item.badge}
                    exact={item.exact}
                    activePaths={item.activePaths}
                  />
                </motion.div>
              ))}
            </SidebarSection>
          ))}
        </AnimatePresence>

      </BaseSidebar>
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        defaultStepId="general"
        productName={productName}
        stepOptions={feedbackStepOptions}
      />
    </>
  );
};
